/* akk-product.js: gallery, variant choice, quantity, the buy bar, add-to-cart
   feedback and recommendations on the product page. Progressive: the strip
   swipes, the thumbnails are anchors and the form posts without any of this.
   Polish P0/P1 (2026-11-01, docs/research/reports/Akkúrat product page polish.md):
   first-frame feedback on "Bæta í körfu" before the cart page, the bar on every
   screen, a variant's photo promoted instead of the page scrolling, the price
   updated at once from the variants JSON, sold-out pills refined per selection. */

(() => {
  'use strict';
  if (window.__akkProductLoaded) return;
  window.__akkProductLoaded = true;
  document.documentElement.classList.add('akk-js');

  const root = document.querySelector('[data-akk-product]');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- gallery ---------- */
  const gallery = root && root.querySelector('[data-akk-gallery]');
  if (gallery) {
    const strip = gallery.querySelector('[data-akk-strip]');
    const items = () => Array.from(strip.querySelectorAll('.akk-gal__item'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-akk-thumb]'));
    const indexEl = gallery.querySelector('[data-akk-gal-index]');

    /* stacked (wide screens, "hver undir annarri"): the strip does not scroll sideways, the page does */
    const stacked = () => items().length > 1 && strip.scrollWidth <= strip.clientWidth + 1;
    const goTo = (item) => {
      if (!item) return;
      const behavior = reducedMotion() ? 'auto' : 'smooth';
      if (stacked()) { item.scrollIntoView({ block: 'start', behavior }); return; }
      strip.scrollTo({ left: item.offsetLeft - strip.offsetLeft, behavior });
    };
    const current = () => {
      const x = strip.scrollLeft + strip.clientWidth / 2;
      return items().findIndex((li) => x >= li.offsetLeft - strip.offsetLeft && x < li.offsetLeft - strip.offsetLeft + li.offsetWidth);
    };
    const mark = () => {
      const i = Math.max(0, current());
      const id = items()[i] && items()[i].dataset.mediaId;
      thumbs.forEach((a) => { if (a.dataset.akkThumb === id) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current'); });
      if (indexEl) indexEl.textContent = String(i + 1);
    };
    const byId = (id) => items().find((li) => li.dataset.mediaId === String(id));

    thumbs.forEach((a) => a.addEventListener('click', (event) => { event.preventDefault(); goTo(byId(a.dataset.akkThumb)); }));
    const prev = gallery.querySelector('[data-akk-gal-prev]');
    const next = gallery.querySelector('[data-akk-gal-next]');
    if (prev) prev.addEventListener('click', () => goTo(items()[Math.max(0, current() - 1)]));
    if (next) next.addEventListener('click', () => goTo(items()[Math.min(items().length - 1, current() + 1)]));
    let raf = 0;
    strip.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); }, { passive: true });
    strip.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(items()[Math.min(items().length - 1, current() + 1)]); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(items()[Math.max(0, current() - 1)]); }
    });
    if (items().length > 1) strip.setAttribute('tabindex', '0');

    /* ---------- lightbox: native dialog, scroll-snap strip, no library ---------- */
    const box = gallery.querySelector('[data-akk-lb]');
    if (box) {
      const boxStrip = box.querySelector('[data-akk-lb-strip]');
      const boxItems = Array.from(boxStrip.querySelectorAll('.akk-lb__item'));
      const boxIndex = box.querySelector('[data-akk-lb-index]');
      const boxPrev = box.querySelector('[data-akk-lb-prev]');
      const boxNext = box.querySelector('[data-akk-lb-next]');
      const canZoom = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const unzoom = () => boxStrip.querySelectorAll('.is-zoomed').forEach((img) => { img.classList.remove('is-zoomed'); img.style.transformOrigin = ''; });
      const at = () => Math.round(boxStrip.scrollLeft / Math.max(1, boxStrip.clientWidth));
      let seen = 0; /* a closed dialog has no width, so the last photo is remembered here */
      const paint = () => {
        const i = at();
        seen = i;
        if (boxIndex) boxIndex.textContent = String(i + 1);
        if (boxPrev) boxPrev.disabled = i === 0;
        if (boxNext) boxNext.disabled = i === boxItems.length - 1;
      };
      /* the neighbours load ahead, so the next photo is already there when it slides in */
      const ahead = (i) => [i - 1, i, i + 1].forEach((n) => { const img = boxItems[n] && boxItems[n].querySelector('img'); if (img) img.loading = 'eager'; });
      const show = (i, smooth) => {
        const n = Math.max(0, Math.min(boxItems.length - 1, i));
        unzoom();
        ahead(n);
        boxStrip.scrollTo({ left: n * boxStrip.clientWidth, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
      };
      const open = (id) => {
        const i = Math.max(0, boxItems.findIndex((li) => li.dataset.mediaId === String(id)));
        box.showModal();
        show(i, false);
        paint();
        boxStrip.focus({ preventScroll: true });
      };
      strip.addEventListener('click', (event) => {
        const li = event.target.closest('[data-lb]');
        if (li && event.target.tagName === 'IMG') open(li.dataset.mediaId);
      });
      gallery.querySelector('[data-akk-lb-open]').addEventListener('click', () => {
        const li = items()[Math.max(0, current())];
        open(li && li.dataset.lb !== undefined ? li.dataset.mediaId : boxItems[0].dataset.mediaId);
      });
      box.querySelector('[data-akk-lb-close]').addEventListener('click', () => box.close());
      if (boxPrev) boxPrev.addEventListener('click', () => show(at() - 1, true));
      if (boxNext) boxNext.addEventListener('click', () => show(at() + 1, true));
      box.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); show(at() + 1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(at() - 1, true); }
      });
      let boxRaf = 0;
      boxStrip.addEventListener('scroll', () => { cancelAnimationFrame(boxRaf); boxRaf = requestAnimationFrame(() => { paint(); ahead(at()); }); }, { passive: true });
      boxStrip.addEventListener('click', (event) => {
        if (event.target.tagName !== 'IMG') { box.close(); return; }
        if (!canZoom()) return;
        const img = event.target;
        const zoomed = img.classList.toggle('is-zoomed');
        if (zoomed) { const r = img.getBoundingClientRect(); img.style.transformOrigin = `${event.clientX - r.left}px ${event.clientY - r.top}px`; }
      });
      boxStrip.addEventListener('mousemove', (event) => {
        const img = boxStrip.querySelector('.is-zoomed');
        if (!img) return;
        const r = img.parentElement.getBoundingClientRect();
        img.style.transformOrigin = `${((event.clientX - r.left) / r.width) * 100}% ${((event.clientY - r.top) / r.height) * 100}%`;
      });
      /* closing leaves the page on the photo last seen */
      box.addEventListener('close', () => {
        unzoom();
        if (!stacked()) goTo(byId(boxItems[seen].dataset.mediaId));
      });
    }

    gallery.isStacked = stacked;
    gallery.showMedia = (id) => goTo(byId(id));
    /* a variant's photo moves to the first slot (stacked mode) under a view transition; nothing scrolls */
    gallery.promoteMedia = (id) => {
      const item = byId(id);
      if (!item || item === strip.firstElementChild) return;
      const move = () => strip.prepend(item);
      if (typeof document.startViewTransition === 'function' && !reducedMotion()) {
        items().forEach((li) => { li.style.viewTransitionName = 'akk-media-' + li.dataset.mediaId; });
        const transition = document.startViewTransition(move);
        transition.finished.finally(() => items().forEach((li) => { li.style.viewTransitionName = ''; }));
        return;
      }
      move();
    };
  }

  /* ---------- money, from the shop's own format ---------- */
  const money = (cents) => {
    const fmt = (root && root.dataset.moneyFormat) || '{{amount}}';
    const n = (Number(cents) || 0) / 100;
    const group = (dec, thousands, decimal) => {
      const [i, d] = n.toFixed(dec).split('.');
      const g = i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return d ? g + decimal + d : g;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(0, ',', '.');
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
        case 'amount_with_space_separator': return group(2, ' ', ',');
        default: return group(2, ',', '.');
      }
    });
  };

  /* ---------- variant choice ---------- */
  const form = root && root.querySelector('[data-akk-form]');
  const variantsEl = document.querySelector('[data-akk-variants]');
  const addButtons = () => [root && root.querySelector('[data-akk-add]'), document.querySelector('[data-akk-bar-add]')].filter(Boolean);
  const setButton = (button, available, label) => {
    if (!button) return;
    button.disabled = !available;
    button.classList.toggle('akk-pdp__add--out', !available);
    const span = button.querySelector('[data-akk-add-label]');
    if (span) span.textContent = label; else button.textContent = label;
  };
  const announceEl = root && root.querySelector('[data-akk-announce]');
  const announce = (text) => {
    if (!announceEl) return;
    announceEl.textContent = '';
    requestAnimationFrame(() => { announceEl.textContent = text; });
  };
  let adding = false;

  if (form && variantsEl) {
    let variants = [];
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
    const idInput = form.querySelector('[data-akk-variant-id]');
    const options = () => Array.from(form.querySelectorAll('[data-akk-option]'));
    const positions = ['1', '2', '3'];
    const labels = { add: root.dataset.addLabel, soldout: root.dataset.soldoutLabel, unavailable: root.dataset.unavailableLabel };

    const selected = () => {
      const chosen = {};
      options().forEach((el) => {
        const pos = el.dataset.optionPosition;
        if (el.tagName === 'SELECT') chosen[pos] = el.value;
        else if (el.checked) chosen[pos] = el.value;
      });
      return chosen;
    };
    const match = () => {
      const chosen = selected();
      return variants.find((v) => positions.every((p) => chosen[p] === undefined || v['option' + p] === chosen[p]));
    };

    /* a pill is struck when no available variant carries its value together with the other chosen values */
    const refinePills = () => {
      const chosen = selected();
      form.querySelectorAll('input[data-akk-option]').forEach((input) => {
        const pos = input.dataset.optionPosition;
        const pill = form.querySelector(`label[for="${input.id}"]`);
        if (!pill) return;
        const ok = variants.some((v) => v.available && v['option' + pos] === input.value && positions.every((p) => p === pos || chosen[p] === undefined || v['option' + p] === chosen[p]));
        pill.classList.toggle('akk-opt__pill--out', !ok);
        let suffix = pill.querySelector('[data-akk-pill-out]');
        if (!ok && !suffix) {
          suffix = document.createElement('span');
          suffix.className = 'visually-hidden';
          suffix.setAttribute('data-akk-pill-out', '');
          suffix.textContent = ` (${labels.soldout})`;
          pill.appendChild(suffix);
        }
        if (ok && suffix) suffix.remove();
      });
    };

    const priceEl = root.querySelector('[data-akk-price]');
    const barPrice = document.querySelector('[data-akk-bar-price]');
    const renderPrice = (variant) => {
      const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const amount = money(variant.price);
      if (priceEl) {
        priceEl.innerHTML = (sale ? '<span class="visually-hidden">Tilboðsverð</span>' : '')
          + `<span class="akk-pdp__amount">${amount}</span>`
          + (sale ? `<s class="akk-pdp__compare"><span class="visually-hidden">Fyrra verð</span>${money(variant.compare_at_price)}</s>` : '');
      }
      if (barPrice) barPrice.textContent = amount;
      return amount;
    };

    /* the server's rendering replaces the optimistic one: Shopify's money format, pickup and stock */
    const swap = (doc) => {
      [['[data-akk-price]', priceEl], ['[data-akk-promises]', root.querySelector('[data-akk-promises]')], ['[data-akk-bar-price]', barPrice]].forEach(([sel, a]) => {
        const b = doc.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
      });
      if (adding) return;
      const fresh = doc.querySelector('[data-akk-add]');
      if (!fresh) return;
      const span = fresh.querySelector('[data-akk-add-label]');
      const label = (span ? span.textContent : fresh.textContent).trim();
      addButtons().forEach((b) => setButton(b, !fresh.disabled, label));
    };

    let pending = null;
    let staleTimer = 0;
    const apply = () => {
      const variant = match();
      refinePills();
      if (!variant) {
        addButtons().forEach((b) => setButton(b, false, labels.unavailable));
        return;
      }
      idInput.value = variant.id;
      const amount = renderPrice(variant);
      if (!adding) addButtons().forEach((b) => setButton(b, variant.available, variant.available ? labels.add : labels.soldout));
      announce(`${variant.title}: ${amount}`);
      const url = new URL(root.dataset.akkUrl, location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.pathname + url.search);
      if (variant.featured_media && gallery) {
        if (gallery.isStacked && gallery.isStacked()) gallery.promoteMedia(variant.featured_media.id);
        else gallery.showMedia(variant.featured_media.id);
      }

      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', root.dataset.akkSection);
      const token = {};
      pending = token;
      clearTimeout(staleTimer);
      staleTimer = setTimeout(() => { if (pending === token && priceEl) priceEl.classList.add('is-stale'); }, 300);
      fetch(sectionUrl.toString())
        .then((res) => res.text())
        .then((html) => { if (pending === token) swap(parse(html)); })
        .catch(() => {})
        .finally(() => { if (pending === token) { clearTimeout(staleTimer); if (priceEl) priceEl.classList.remove('is-stale'); } });
    };
    form.addEventListener('change', (event) => { if (event.target.matches('[data-akk-option]')) apply(); });
    refinePills();

    /* ---------- add to cart: feedback in the first frame, then the cart page ---------- */
    // The decision to land on the cart page stands (no drawer). What changes is the second before it:
    // the button is busy at once, the header count bumps when the line is in, "Bætt í körfu" is held
    // 600ms, then the page follows. Without fetch, or when the request fails, the form posts as before.
    const action = new URL(form.getAttribute('action') || '/cart/add', location.href);
    const cartPage = action.pathname.replace(/\/add\/?$/, '');
    const addUrl = `${cartPage}/add.js`;
    const status = form.querySelector('[data-akk-status]');
    const refreshHeader = () => fetch(`${cartPage}?sections=akk-header`)
      .then((res) => res.json())
      .then((sections) => {
        const fresh = parse(sections['akk-header'] || '').querySelector('.akk-hdr__cart');
        if (!fresh) return;
        document.querySelectorAll('.akk-hdr__cart').forEach((el) => { el.innerHTML = fresh.innerHTML; });
      })
      .catch(() => {});
    const setState = (state) => addButtons().forEach((b) => { if (state) b.dataset.state = state; else delete b.dataset.state; });
    const setLabel = (text) => addButtons().forEach((b) => { const span = b.querySelector('[data-akk-add-label]'); if (span) span.textContent = text; });
    const showStatus = (text) => { if (!status) return; status.textContent = text; status.hidden = false; };
    const restore = () => {
      adding = false;
      setState(null);
      addButtons().forEach((b) => { b.disabled = false; b.removeAttribute('aria-busy'); });
      setLabel(labels.add);
    };
    form.addEventListener('submit', (event) => {
      if (!window.fetch || form.dataset.akkNative === '1' || adding) { if (adding) event.preventDefault(); return; }
      event.preventDefault();
      adding = true;
      const first = addButtons()[0];
      const addedLabel = (first && first.dataset.addedLabel) || 'Bætt í körfu';
      const addingLabel = (first && first.dataset.addingLabel) || '';
      if (status) { status.hidden = true; status.textContent = ''; }
      addButtons().forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      setState('busy');
      if (reducedMotion() && addingLabel) setLabel(addingLabel);
      const data = new FormData();
      data.append('id', idInput.value);
      const qty = form.querySelector('[name="quantity"]');
      data.append('quantity', (qty && qty.value) || '1');
      form.querySelectorAll('[name^="properties["], [name="selling_plan"]').forEach((el) => data.append(el.name, el.value));
      fetch(addUrl, { method: 'POST', body: data, headers: { Accept: 'application/json' } })
        .then(async (res) => {
          if (res.ok) {
            await refreshHeader();
            setState('done');
            setLabel(addedLabel);
            announce(addedLabel);
            setTimeout(() => location.assign(cartPage), 600);
            return;
          }
          let message = (status && status.dataset.stockError) || labels.unavailable;
          if (res.status !== 422) {
            try { const body = await res.json(); message = body.description || body.message || message; } catch (e) { /* keep the theme's line */ }
          }
          restore();
          showStatus(message);
        })
        .catch(() => {
          restore();
          form.dataset.akkNative = '1';
          HTMLFormElement.prototype.submit.call(form);
        });
    });
  }

  /* ---------- quantity ---------- */
  document.addEventListener('click', (event) => {
    const minus = event.target.closest('[data-akk-qty-minus]');
    const plus = event.target.closest('[data-akk-qty-plus]');
    if (!minus && !plus) return;
    const box = (minus || plus).closest('[data-akk-qty]');
    const input = box && box.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    const value = parseInt(input.value || '1', 10) || min;
    input.value = String(Math.max(min, value + (plus ? 1 : -1)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- buy panel taller than the screen ---------- */
  // A panel that fits sticks at 24px. A taller one flows with the page (sticky at the top would hide
  // its lower part until the photos end) and the buy bar carries the button meanwhile.
  const sticky = root && root.querySelector('[data-akk-sticky]');
  if (sticky) {
    const fit = () => sticky.classList.toggle('akk-pdp__sticky--flow', sticky.offsetHeight > window.innerHeight - 48);
    fit();
    window.addEventListener('resize', fit);
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(sticky);
    else sticky.addEventListener('toggle', fit, true);
  }

  /* ---------- the buy bar ---------- */
  // Outside the section so it can be fixed; found document-wide. Shown once the inline button has
  // scrolled above the viewport, hidden while the footer is within 200px; the cookie notice and the
  // wishlist drawer hide it through classes on <html> (CSS).
  const bar = document.querySelector('[data-akk-bar]');
  const add = root && root.querySelector('[data-akk-add]');
  if (bar && add && 'IntersectionObserver' in window) {
    let passed = false;
    let nearFooter = false;
    const update = () => { bar.hidden = !(passed && !nearFooter); };
    new IntersectionObserver((entries) => {
      const entry = entries[0];
      passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }, { threshold: 0 }).observe(add);
    const footer = document.querySelector('.akk-ft, footer');
    if (footer) {
      new IntersectionObserver((entries) => { nearFooter = entries[0].isIntersecting; update(); }, { rootMargin: '0px 0px 200px 0px' }).observe(footer);
    }
  }

  /* ---------- the client's description links ---------- */
  // Pasted "Nánar" links carry search tracking parameters and a random target; strip the first,
  // open only other sites in a new tab.
  if (root) {
    root.querySelectorAll('.akk-pdp__desc a[href]').forEach((a) => {
      try {
        const url = new URL(a.getAttribute('href'), location.href);
        let changed = false;
        ['_pos', '_sid', '_ss', '_psq', '_fid'].forEach((key) => { if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; } });
        const internal = url.origin === location.origin;
        if (changed) a.setAttribute('href', internal ? url.pathname + url.search + url.hash : url.toString());
        if (internal) a.removeAttribute('target');
        else { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener'); }
      } catch (e) { /* leave the link as written */ }
    });
  }

  /* ---------- recommendations ---------- */
  document.querySelectorAll('[data-akk-recs][data-akk-url]').forEach((box) => {
    fetch(box.dataset.akkUrl)
      .then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); })
      .then((html) => {
        const fresh = parse(html).querySelector('[data-akk-recs]');
        if (fresh && fresh.innerHTML.trim()) box.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  });
})();
